import { validate } from '@angular/forms/signals';
import type { ObjectSchemaPathTree } from '@indutrust/shared-util';

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Schema fragment that ensures the available workshop area never exceeds the
 * total workshop area.
 *
 * Targets the `workshop_available` field so the error surfaces under the
 * input the user is interacting with, while reading `workshop_used` via
 * {@link validate}'s `valueOf` so editing either side of the pair
 * re-evaluates the constraint.
 *
 * The check is only performed when both values are finite numbers — leaving
 * either field blank is a normal in-progress state and is handled by the
 * required validators when the product needs them.
 *
 * @param p A schema path tree containing `workshop_used` and `workshop_available` fields.
 * @example
 * form(model, (p) => {
 *   workshopAreaValidator(p);
 * });
 */
export function workshopAreaValidator<
  T extends {
    workshop_used: number | null;
    workshop_available: number | null;
  },
>(p: ObjectSchemaPathTree<T>): void {
  validate(p.workshop_available, (ctx) => {
    const used = toFiniteNumber(ctx.valueOf(p.workshop_used));
    const available = toFiniteNumber(ctx.value());

    if (used === null || available === null) {
      return null;
    }

    return available > used ? { kind: 'workshopAreaExceedsTotal' } : null;
  });
}
